// /ChatFeedManager/PlatformCannon.jsx
import { authManager } from '@/managers/AuthManager';
import { getApiBaseUrl } from '@/utils/setProdOrDev';

const LOG_PREFIX = '[PlatformCannon]';

// ========================
// ERROR_MESSAGES - Standard error messages for API interactions
// ========================
const ERROR_MESSAGES = {
  AUTHENTICATION: 'User not authenticated. Please sign in again.',
  API_KEY_MISSING: 'API key missing. Please add a valid key in settings.',
  INVALID_API_KEY: 'Invalid API key. Please verify your key in settings.',
  NO_API_CREDITS: 'API credits depleted. Please add funds to your account.',
  RATE_LIMIT: 'API rate limit exceeded. Please try again later.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  OVERLOADED_ERROR: 'The service is currently overloaded. Please try again in a few moments.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.'
};

/* ========================
 * parseUpstreamError - Standardizes errors from backend/LLM responses
 * ========================*/
const parseUpstreamError = (errorPayload) => {
  if (!errorPayload) {
      return { message: ERROR_MESSAGES.UNKNOWN_ERROR, code: 'UNKNOWN_UPSTREAM_ERROR' };
  }

  let parsedPayload = errorPayload;
  if (typeof errorPayload === 'string') {
      const jsonRegex = /\{.*\}/s;
      const jsonMatch = errorPayload.match(jsonRegex);
      
      if (jsonMatch) {
          try {
              parsedPayload = JSON.parse(jsonMatch[0]);
          } catch (e) {
              try {
                  parsedPayload = JSON.parse(errorPayload);
              } catch (e2) {
                  const cleanMessage = errorPayload.replace(/^Error processing .+?: \d+ /, '');
                  return { message: cleanMessage, code: 'RAW_STRING_ERROR' };
              }
          }
      } else {
          try {
              parsedPayload = JSON.parse(errorPayload);
          } catch (e) {
              return { message: errorPayload, code: 'RAW_STRING_ERROR' };
          }
      }
  }

  if (typeof parsedPayload === 'object' && parsedPayload !== null) {
      let errorSource = parsedPayload.error || parsedPayload;
      
      if (errorSource.error && typeof errorSource.error === 'object') {
          errorSource = errorSource.error;
      }
      
      const message = errorSource.message;
      const code = errorSource.type || errorSource.code;

      if (code === 'overloaded_error') {
          return { message: ERROR_MESSAGES.OVERLOADED_ERROR, code };
      }
      
      if (code === 'invalid_request_error' && message) {
          return { message, code };
      }
      
      const finalMessage = message || JSON.stringify(parsedPayload);
      const finalCode = code || 'UNKNOWN_UPSTREAM_CODE';
      
      return { message: finalMessage, code: finalCode };
  }

  return { message: String(errorPayload), code: 'UNKNOWN_UPSTREAM_ERROR_TYPE' };
};

// ========================
// PlatformCannon - Manages sending requests to the unified cloud service endpoint
// ========================
class PlatformCannon {
  /* ========================
   * constructor - Initializes the PlatformCannon instance
   * ========================*/
  constructor() {
    this.cloudRunUrl = getApiBaseUrl();
    this.activeRequests = new Map();
  }

  /* ========================
   * getAuthHeaders - Automatically injects Firebase token into request headers
   * This is the CENTRALIZED auth injection point for all PlatformCannon calls.
   * ========================*/
  async getAuthHeaders() {
    const currentUser = authManager.getCurrentUser();
    if (!currentUser) {
      throw { 
        message: ERROR_MESSAGES.AUTHENTICATION, 
        code: 'AUTHENTICATION_ERROR',
        status: 401 
      };
    }

    try {
      const idToken = await currentUser.getIdToken();
      return {
        'Authorization': `Bearer ${idToken}`
      };
    } catch (error) {
      throw { 
        message: 'Failed to retrieve authentication token. Please sign in again.',
        code: 'TOKEN_RETRIEVAL_ERROR',
        status: 401 
      };
    }
  }

  /* ========================
   * fixFormatting - Calls the backend to fix message markdown
   * ======================== */
  async fixFormatting(payload) {
    const requestId = `fix_${crypto.randomUUID()}`;
    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${this.cloudRunUrl}/fix-formatting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': requestId,
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ message: `Server error: ${response.statusText}`}));
        return { success: false, error: { ...errorBody, status: response.status } };
      }

      const result = await response.json();
      return { success: true, ...result };
    } catch (error) {
      return {
        success: false,
        error: { message: error.message, code: error.code || 'NETWORK_ERROR' },
      };
    }
  }

  /* ========================
   * invokeService - Direct API calls for testing and simple requests
   * ========================*/
  async invokeService(endpoint, payload, options = {}) {
    const requestId = options.requestId || crypto.randomUUID();
    const { workspaceContextId } = options;

    const finalPayload = { ...payload };

    if (!finalPayload.userId) {
        const currentUser = authManager.getCurrentUser();
        if (currentUser?.uid) {
            finalPayload.userId = currentUser.uid;
        }
    }

    finalPayload.options = {
        ...(finalPayload.options || {}),
        ...(workspaceContextId && { workspaceContextId }),
    };

    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${this.cloudRunUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': requestId,
          ...authHeaders,
        },
        body: Object.keys(finalPayload).length > 0 ? JSON.stringify(finalPayload) : undefined,
      });

      if (!response.ok) {
        let errorData = {};
        let errorMessage = 'An error occurred during service invocation.';
        let errorCode = `HTTP_${response.status}`;
        let responseBodyText = '';

        try {
          responseBodyText = await response.text();
          const parsedBody = JSON.parse(responseBodyText);
          errorData = parsedBody.error || parsedBody;
          errorMessage = errorData.message || responseBodyText;
          errorCode = errorData.code || `HTTP_${response.status}`;

          const jsonInMessageRegex = /({.*})/;
          const match = typeof errorMessage === 'string' && errorMessage.match(jsonInMessageRegex);
          if (match && match[1]) {
            try {
              const nestedJson = JSON.parse(match[1]);
              const deeperError = nestedJson.error || nestedJson;
              if (deeperError.message) {
                errorMessage = deeperError.message;
                if (deeperError.type) errorCode = deeperError.type;
                else if (deeperError.code) errorCode = deeperError.code;
              }
            } catch (e) { /* ignore parse error of nested content */ }
          }
        } catch (e) {
          errorMessage = responseBodyText || `Server error: ${response.statusText}`;
          errorData = { rawResponse: responseBodyText };
        }

        return {
          success: false,
          error: {
            message: errorMessage,
            status: response.status,
            code: errorCode,
            originalErrorData: errorData
          },
          status: response.status
        };
      }

      const result = await response.json();

      if (result && result.error) {
        return {
          success: false,
          error: {
            message: result.error.message || 'The downstream service returned an error within a successful response.',
            code: result.error.code || 'DOWNSTREAM_ERROR_IN_200',
            originalErrorData: result.error
          },
          status: response.status
        };
      }
      
      return { success: true, ...result, status: response.status };

    } catch (error) {
      return {
        success: false,
        error: {
          message: error?.message || 'Network error occurred',
          code: error?.code || 'NETWORK_ERROR',
          originalError: error
        }
      };
    }
  }

  /* ========================
   * abortStream - Calls the backend to abort a stream
   * ======================== */
  async abortStream(assistantMessageId, userId) {
    try {
        const authHeaders = await this.getAuthHeaders();
        const response = await fetch(`${this.cloudRunUrl}/abort-stream`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify({ assistantMessageId, userId }),
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({ message: 'Failed to decode error response.' }));
            return { success: false, error: errorBody };
        }
        
        return { success: true };
    } catch (error) {
        return { success: false, error: { message: error.message, code: error.code || 'NETWORK_ERROR' } };
    }
  }
  
  /* ========================
   * sendRequest - Main method to send requests to the unified endpoint
   * ========================*/
  async sendRequest(platform, jsonPayloadInput, filesToSend = [], optionsFromCfm = {}) {
    const finalJsonPayload = JSON.parse(JSON.stringify(jsonPayloadInput));
    const requestId = optionsFromCfm.streamId || finalJsonPayload.assistantMessageId || crypto.randomUUID();

    const controller = new AbortController();
    this.activeRequests.set(requestId, {
        controller, platform, timestamp: Date.now(),
        type: finalJsonPayload.options.streamToClient ? 'stream-client-handling' : 'non-stream-client-wait'
    });

    let linkedSignal = controller.signal;
    if (optionsFromCfm.abortSignal) {
        const externalSignal = optionsFromCfm.abortSignal;
        if (externalSignal.aborted && !controller.signal.aborted) {
            controller.abort();
        } else {
            linkedSignal = this.linkAbortSignals(controller.signal, externalSignal, requestId);
        }
    }

    try {
      if (!finalJsonPayload.userId) {
         const currentUser = authManager.getCurrentUser();
         if (currentUser?.uid) {
            finalJsonPayload.userId = currentUser.uid;
         } else {
            throw { message: ERROR_MESSAGES.AUTHENTICATION, code: 'AUTHENTICATION_ERROR_PAYLOAD', status: 401 };
         }
      }

      const formData = new FormData();
      formData.append('json_payload', JSON.stringify(finalJsonPayload));
      if (filesToSend?.length > 0) {
        filesToSend.forEach((fileItem) => {
          if (fileItem.blob instanceof Blob) {
            formData.append('files', fileItem.blob, fileItem.originalname);
          }
        });
      }

      const endpoint = optionsFromCfm.useStructuredEndpoint ? '/stream-chat-structured' : '/stream-chat';
      const url = `${this.cloudRunUrl}${endpoint}`;
      
      const result = await this.handleStreamRequestInternal(
          url, formData, optionsFromCfm, requestId, controller, linkedSignal,
          finalJsonPayload.options.streamToClient, finalJsonPayload
      );
      
      this.activeRequests.delete(requestId);
      return result;

    } catch (error) {
      const message = error?.message || (typeof error === 'string' ? error : 'An unknown error occurred in PlatformCannon.');
      const code = error?.code || 'CANNON_CLIENT_ERROR';
      const status = error?.status || 500;
      
      const finalError = new Error(message);
      finalError.code = code;
      finalError.status = status;
      finalError.originalError = error;
      finalError.originalErrorData = error?.originalErrorData;

      if (error?.name === 'AbortError') {
          finalError.message = 'Request cancelled by user.';
          finalError.code = 'USER_CANCELLED';
          this.activeRequests.delete(requestId);
          return { cancelled: true, streamId: requestId, success: false, error: finalError };
      }

      this.activeRequests.delete(requestId);
      return { success: false, error: finalError, streamId: requestId };
    }
  }

  linkAbortSignals(primarySignal, secondarySignal, requestId) {
    const onAbort = () => {
      const requestData = this.activeRequests.get(requestId);
      if(requestData?.controller && !requestData.controller.signal.aborted) {
          requestData.controller.abort();
      }
      if (secondarySignal.removeEventListener) {
        secondarySignal.removeEventListener('abort', onAbort);
      }
    };
    if (secondarySignal.addEventListener) {
        secondarySignal.addEventListener('abort', onAbort, { once: true });
        primarySignal.addEventListener('abort', () => {
            secondarySignal.removeEventListener('abort', onAbort);
        }, { once: true });
    }
    return primarySignal;
  }

  /* ========================
   * handleStreamRequestInternal - Handles SSE streams with proper event boundary detection
   * ======================== */
  async handleStreamRequestInternal(
    url, formData, clientSideOptions, requestId, controller, signalForFetch,
    serverShouldStream, originalJsonPayload
  ) {
    let fullContent = '';
    let finalUsage = null;
    let streamError = null;

    try {
      performance.mark(`trace-${requestId}-start`);

      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(url, {
        method: 'POST',
        signal: signalForFetch,
        headers: authHeaders,
        body: formData
      });

      if (!response.ok) {
        let responseBodyText = '';
        try {
            responseBodyText = await response.text();
        } catch (readError) {
            throw { message: response.statusText, status: response.status, code: `HTTP_ERROR_${response.status}` };
        }

        const { message, code } = parseUpstreamError(responseBodyText);
        throw { message, status: response.status, code, originalErrorData: { rawResponse: responseBodyText } };
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamEnded = false;
      let textBlockId = null;

      while (!streamEnded) {
        if (signalForFetch.aborted) throw new DOMException('Aborted by client/signal', 'AbortError');
        const { done, value } = await reader.read();

        if (done) {
          streamEnded = true;
        }
        
        buffer += decoder.decode(value || new Uint8Array(), { stream: true });
        
        let eventEnd;
        while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
          const completeEvent = buffer.substring(0, eventEnd + 2);
          buffer = buffer.substring(eventEnd + 2);
          
          const lines = completeEvent.split('\n');
          for (const line of lines) {
            if (!line.trim()) continue;

            if (line.startsWith('data: ')) {
              const dataString = line.substring(6).trim();
              
              const isDone = dataString === '[DONE]' || dataString === '"[DONE]"';
              const isDoneWithError = dataString === '[DONE_WITH_ERROR]' || dataString === '"[DONE_WITH_ERROR]"';

              if (isDone || isDoneWithError) {
                if (clientSideOptions.onChunk) {
                    const endChunk = { type: 'stream_end', payload: { reason: isDone ? 'complete' : 'error' } };
                    performance.mark(`trace-${requestId}-chunk-to-cfm`);
                    clientSideOptions.onChunk({ chunk: endChunk, streamId: clientSideOptions.streamId || requestId });
                }
                
                streamEnded = true; 
                break;
              }

              if (dataString) {
                try {
                  const parsedChunk = JSON.parse(dataString);
                  const rawType = parsedChunk.type;
                  const sid = clientSideOptions.streamId || requestId;

                  // ── New server schema (text_chunk / stream_end / tool_start / tool_result) ──
                  if (rawType === 'text_chunk') {
                    const text = parsedChunk.payload?.text || '';
                    fullContent += text;
                    if (clientSideOptions.onChunk) {
                      performance.mark(`trace-${requestId}-chunk-to-cfm`);
                      if (!textBlockId) {
                        textBlockId = `text_${crypto.randomUUID()}`;
                        clientSideOptions.onChunk({
                          chunk: { type: 'BLOCK_CREATE', payload: { type: 'text', id: textBlockId, content: '' } },
                          streamId: sid
                        });
                      }
                      clientSideOptions.onChunk({
                        chunk: { type: 'BLOCK_DELTA', payload: { id: textBlockId, contentChunk: text } },
                        streamId: sid
                      });
                    }
                  } else if (rawType === 'stream_end') {
                    if (parsedChunk.payload?.usage) finalUsage = parsedChunk.payload.usage;
                    if (parsedChunk.payload?.fullContent) fullContent = parsedChunk.payload.fullContent;
                    if (clientSideOptions.onChunk) {
                      performance.mark(`trace-${requestId}-chunk-to-cfm`);
                      clientSideOptions.onChunk({
                        chunk: { type: 'STREAM_END', payload: parsedChunk.payload || {} },
                        streamId: sid
                      });
                    }
                    streamEnded = true;
                    break;
                  } else if (rawType === 'tool_start') {
                    if (clientSideOptions.onChunk) {
                      performance.mark(`trace-${requestId}-chunk-to-cfm`);
                      clientSideOptions.onChunk({
                        chunk: { type: 'TOOL_CALL_START', payload: parsedChunk.payload },
                        streamId: sid
                      });
                    }
                  } else if (rawType === 'tool_result') {
                    if (clientSideOptions.onChunk) {
                      performance.mark(`trace-${requestId}-chunk-to-cfm`);
                      clientSideOptions.onChunk({
                        chunk: { type: 'TOOL_PROCESSING_RESULT', payload: parsedChunk.payload },
                        streamId: sid
                      });
                    }
                  } else {
                    // ── Legacy / passthrough: content_delta, usage_update, rich BLOCK_* events, etc. ──
                    if (rawType === 'usage_update') {
                      finalUsage = parsedChunk.payload;
                    } else if (rawType === 'content_delta') {
                      fullContent += parsedChunk.payload?.text || '';
                    } else if (rawType === 'stream_error') {
                      const { message, code } = parseUpstreamError(parsedChunk.payload);
                      streamError = new Error(message);
                      streamError.code = code;
                      streamError.originalErrorData = parsedChunk.payload;
                    }

                    if (clientSideOptions.onChunk) {
                      performance.mark(`trace-${requestId}-chunk-to-cfm`);
                      const normalizedChunk = {
                        ...parsedChunk,
                        type: typeof rawType === 'string' ? rawType.toUpperCase() : rawType
                      };
                      if (rawType === 'stream_error' && streamError) {
                        normalizedChunk.payload = { message: streamError.message, code: streamError.code };
                      }
                      clientSideOptions.onChunk({ chunk: normalizedChunk, streamId: sid });
                    }
                  }

                } catch (e) { 
                  const contentPreview = dataString ? dataString.substring(0, 100) : 'null/undefined';
                  const parseError = new Error(`Failed to parse stream chunk. Content: "${contentPreview}..."`);
                  parseError.code = 'STREAM_PARSE_ERROR';
                  parseError.originalJsonParseError = e;
                  parseError.data = dataString;
                  throw parseError;
                }
              }
            }
          }
          if (streamEnded) break;
        }
      } 

      if (streamError) {
          throw streamError;
      }
      
      performance.mark(`trace-${requestId}-end`);
      performance.measure(`Trace Duration for ${requestId}`, `trace-${requestId}-start`, `trace-${requestId}-end`);

      return {
          success: true,
          fullContent: fullContent,
          usage: finalUsage,
          streamId: clientSideOptions.streamId || requestId,
          userMessageId: originalJsonPayload.userMessageId, 
          assistantMessageId: originalJsonPayload.assistantMessageId,
          cancelled: signalForFetch.aborted 
      };

    } catch (error) { 
      throw error;
    }
  }

  cancelRequest(requestId) {
    try {
      const request = this.activeRequests.get(requestId);
      if (request?.controller && !request.controller.signal.aborted) {
        request.controller.abort();
      }
    } catch (error) { /* Silently fail */ }
  }

  cancelAllRequests() {
    this.activeRequests.forEach((request) => {
      try {
        if (request.controller && !request.controller.signal.aborted) {
            request.controller.abort();
        }
      } catch (error) { /* Silently fail */ }
    });
    this.activeRequests.clear();
  }
}

export const platformCannon = new PlatformCannon();
export const Platforms = ["OPENAI", "CLAUDE", "GEMINI", "MISTRAL", "OPENROUTER", "GROK"];
export default PlatformCannon;